import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '../../../api'
import LoanTab from './LoanTab'
import RequestTab from './RequestTab'

// ===================== 타입 =====================
interface BookListItem {
    book_code: string | null
    title: string | null
    author: string | null
    total_count: number
    available_count: number
    loaned_count: number
}

interface LoanListItem {
    loan_id: number
    member_id: number
    member_name: string | null
    book_title: string
    barcode: string
    loaned_at: string
    returned_at: string | null
    status: string
}

interface BookRequestItem {
    id: number
    member_id: number
    book_title: string
    reason: string | null
    status: number
    created_at: string
}

interface BulkFailure {
    row: number
    barcode: string | null
    reason: string
}

const TABS = [
    { key: 'book', label: '도서관리' },
    { key: 'loan', label: '대출/반납 관리' },
    { key: 'request', label: '책 신청 관리' },
] as const

type TabKey = typeof TABS[number]['key']

export default function AdminLibraryManagement() {
    const navigate = useNavigate()
    const [activeTab, setActiveTab] = useState<TabKey>('book')

    return (
        <div className="bg-[#f5f2f0] min-h-screen p-6">
            <div className="max-w-[1200px] mx-auto">

                {/* 헤더 */}
                <div className="mb-6">
                    <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">마루책방관리</h1>
                    <p className="text-[13px] text-gray-400">
                        {new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' })}
                    </p>
                </div>

                {/* 탭 */}
                <div className="flex gap-2 mb-5 bg-white rounded-2xl p-1.5 border border-[#e8e0dc] w-fit shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={[
                                'px-5 py-2 rounded-xl text-[13px] font-semibold transition-colors',
                                activeTab === tab.key
                                    ? 'bg-[#887267] text-white'
                                    : 'text-gray-400 hover:text-[#887267]',
                            ].join(' ')}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {activeTab === 'book' && <BookTab navigate={navigate} />}
                {activeTab === 'loan' && <LoanTab />}
                {activeTab === 'request' && <RequestTab />}
            </div>
        </div>
    )
}

// =====================================================
// 도서관리 탭
// =====================================================
function BookTab({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
    const [books, setBooks] = useState<BookListItem[]>([])
    const [loading, setLoading] = useState(true)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const limit = 20

    const [showRegisterModal, setShowRegisterModal] = useState(false)
    const [showBulkModal, setShowBulkModal] = useState(false)
    const [confirmModal, setConfirmModal] = useState<{ open: boolean; bookCode: string | null }>({ open: false, bookCode: null })
    const [resultModal, setResultModal] = useState({ open: false, title: '' })

    const fetchBooks = async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(limit) })
            if (search) params.set('search', search)
            const res = await fetchWithAuth(getApiUrl(`/marubit/api/book/list?${params.toString()}`))
            if (!res.ok) throw new Error('조회 실패')
            const json = await res.json()
            setBooks(json.data.books)
            setTotal(json.data.total)
        } catch {
            setResultModal({ open: true, title: '목록을 불러오는 데 실패했습니다.' })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchBooks() }, [page, search])

    const handleSearch = () => {
        setPage(1)
        setSearch(searchInput.trim())
    }

    // 삭제는 book_code 그룹의 첫 사본을 못 지우므로 상세에서 처리하는 게 맞지만,
    // 목록에서 빠른 삭제를 위해 상세 조회 후 id를 얻어 삭제 (사본이 1개일 때만 안전)
    const handleDeleteClick = (bookCode: string) => setConfirmModal({ open: true, bookCode })

    const handleDeleteConfirm = async () => {
        if (!confirmModal.bookCode) return
        try {
            const token = localStorage.getItem('access_token')
            const detailRes = await fetch(getApiUrl(`/marubit/api/book/${confirmModal.bookCode}`), {
                headers: { Authorization: `Bearer ${token}` },
            })
            const detailJson = await detailRes.json()
            const copies = detailJson?.data?.copies ?? []
            if (copies.length === 0) throw new Error('도서를 찾을 수 없습니다.')

            for (const copy of copies) {
                const res = await fetch(getApiUrl(`/marubit/api/book/admin/${copy.id}`), {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` },
                })
                if (!res.ok) {
                    const err = await res.json()
                    throw new Error(err?.error?.message || '삭제 실패')
                }
            }
            setResultModal({ open: true, title: '도서가 삭제되었습니다.' })
            fetchBooks()
        } catch (e: any) {
            setResultModal({ open: true, title: e.message ?? '삭제에 실패했습니다.' })
        } finally {
            setConfirmModal({ open: false, bookCode: null })
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / limit))

    return (
        <>
            {/* 검색 + 액션 버튼 */}
            <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            value={searchInput}
                            onChange={e => setSearchInput(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleSearch()}
                            placeholder="도서명 또는 저자 검색"
                            className="w-64 h-9 px-3 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]"
                        />
                        <button onClick={handleSearch} className="h-9 px-5 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors">
                            조회
                        </button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setShowBulkModal(true)} className="h-9 px-4 border border-[#887267] text-[#887267] rounded-lg text-[13px] font-semibold hover:bg-[#f5f2f0] transition-colors">
                            엑셀 업로드
                        </button>
                        <button onClick={() => setShowRegisterModal(true)} className="h-9 px-4 bg-[#887267] text-white rounded-lg text-[13px] font-semibold hover:bg-[#6b5248] transition-colors">
                            + 도서 등록
                        </button>
                    </div>
                </div>
            </div>

            {/* 테이블 */}
            <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <table className="w-full">
                    <thead>
                        <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">도서코드</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">도서명</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[140px]">저자</th>
                            <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">대출현황</th>
                            <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[80px]">관리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
                        ) : books.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-300 text-[13px]">등록된 도서가 없습니다.</td></tr>
                        ) : books.map(book => (
                            <tr key={book.book_code} className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors">
                                <td className="px-4 py-3 text-[12px] text-gray-400 font-mono">{book.book_code ?? '-'}</td>
                                <td className="px-4 py-3 text-[13px] font-medium text-[#887267] cursor-pointer hover:underline"
                                    onClick={() => navigate(`/admin/library/book/${book.book_code}`)}>
                                    {book.title}
                                </td>
                                <td className="px-4 py-3 text-[12px] text-gray-400">{book.author ?? '-'}</td>
                                <td className="px-4 py-3 text-center text-[12px] text-gray-500">
                                    {book.available_count}/{book.total_count}권
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <button
                                        onClick={() => handleDeleteClick(book.book_code!)}
                                        className="px-3 py-1 text-[12px] border border-red-200 text-red-400 rounded-lg hover:bg-red-50 transition-colors"
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-5">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        이전
                    </button>
                    <span className="text-[13px] text-gray-500">{page} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        다음
                    </button>
                </div>
            )}

            {/* 도서 등록 모달 */}
            {showRegisterModal && (
                <BookRegisterModal
                    onClose={() => setShowRegisterModal(false)}
                    onSuccess={() => { setShowRegisterModal(false); fetchBooks(); setResultModal({ open: true, title: '도서가 등록되었습니다.' }) }}
                />
            )}

            {/* 엑셀 업로드 모달 */}
            {showBulkModal && (
                <BookBulkUploadModal
                    onClose={() => setShowBulkModal(false)}
                    onSuccess={() => { fetchBooks() }}
                />
            )}

            {/* 삭제 확인 모달 */}
            {confirmModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">이 도서를 삭제하시겠습니까?</h3>
                        <p className="text-[13px] text-gray-400 mb-5">대출 중인 사본이 있으면 삭제할 수 없습니다.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmModal({ open: false, bookCode: null })}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleDeleteConfirm}
                                className="flex-1 px-4 py-2 bg-red-400 text-white rounded-lg hover:bg-red-500 text-[13px] font-semibold">삭제</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 결과 모달 */}
            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{resultModal.title}</p>
                        <button onClick={() => setResultModal({ open: false, title: '' })}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
                    </div>
                </div>
            )}
        </>
    )
}

// ===================== 도서 등록 모달 =====================
function BookRegisterModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const [barcode, setBarcode] = useState('')
    const [bookCode, setBookCode] = useState('')
    const [title, setTitle] = useState('')
    const [author, setAuthor] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleSubmit = async () => {
        if (!barcode.trim()) { setError('바코드를 입력해주세요.'); return }
        if (!title.trim()) { setError('도서명을 입력해주세요.'); return }
        setError('')
        setSubmitting(true)
        try {
            const token = localStorage.getItem('access_token')
            const res = await fetch(getApiUrl('/marubit/api/book/admin'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    barcode: barcode.trim(),
                    book_code: bookCode.trim() || null,
                    title: title.trim(),
                    author: author.trim() || null,
                }),
            })
            if (!res.ok) {
                const err = await res.json()
                throw new Error(err?.error?.message || '등록 실패')
            }
            onSuccess()
        } catch (e: any) {
            setError(e.message ?? '등록에 실패했습니다.')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[420px] mx-4 p-6">
                <h3 className="text-[17px] font-bold text-[#2a1f1a] mb-5">도서 등록</h3>

                <div className="flex flex-col gap-3">
                    <div>
                        <label className="block text-[12px] text-gray-400 mb-1">바코드 *</label>
                        <input value={barcode} onChange={e => setBarcode(e.target.value)}
                            className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                    </div>
                    <div>
                        <label className="block text-[12px] text-gray-400 mb-1">도서코드</label>
                        <input value={bookCode} onChange={e => setBookCode(e.target.value)}
                            className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                    </div>
                    <div>
                        <label className="block text-[12px] text-gray-400 mb-1">도서명 *</label>
                        <input value={title} onChange={e => setTitle(e.target.value)}
                            className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                    </div>
                    <div>
                        <label className="block text-[12px] text-gray-400 mb-1">저자</label>
                        <input value={author} onChange={e => setAuthor(e.target.value)}
                            className="w-full px-3 py-2 border border-[#e8e0dc] rounded-lg text-[13px] outline-none focus:border-[#887267]" />
                    </div>
                </div>

                {error && <p className="text-[12px] text-red-400 mt-3">{error}</p>}

                <div className="flex gap-2 mt-6">
                    <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold disabled:opacity-50">
                        {submitting ? '등록 중...' : '등록'}
                    </button>
                </div>
            </div>
        </div>
    )
}

// ===================== 엑셀 업로드 모달 =====================
function BookBulkUploadModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
    const [file, setFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const [result, setResult] = useState<{ total: number; success_count: number; fail_count: number; failures: BulkFailure[] } | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const handleUpload = async () => {
        if (!file) return
        setUploading(true)
        try {
            const token = localStorage.getItem('access_token')
            const fd = new FormData()
            fd.append('file', file)
            const res = await fetch(getApiUrl('/marubit/api/book/admin/bulk'), {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json?.error?.message || '업로드 실패')
            setResult(json.data)
            onSuccess()
        } catch (e: any) {
            alert(e.message ?? '업로드에 실패했습니다.')
        } finally {
            setUploading(false)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4 p-6">
                <h3 className="text-[17px] font-bold text-[#2a1f1a] mb-2">엑셀 일괄 등록</h3>
                <p className="text-[12px] text-gray-400 mb-4">
                    엑셀 컬럼 순서: barcode, book_code, title, author (.xlsx)
                </p>

                {!result ? (
                    <>
                        <div
                            onClick={() => fileInputRef.current?.click()}
                            className="border-2 border-dashed border-[#e8e0dc] rounded-xl h-28 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-[#887267] transition-colors"
                        >
                            <p className="text-[13px] text-gray-400">{file ? file.name : '파일을 선택해주세요'}</p>
                            <span className="px-3 py-1 border border-[#887267] text-[#887267] rounded-lg text-[12px]">파일 선택</span>
                        </div>
                        <input ref={fileInputRef} type="file" accept=".xlsx,.xlsm,.xltx,.xltm" className="hidden"
                            onChange={e => setFile(e.target.files?.[0] ?? null)} />

                        <div className="flex gap-2 mt-6">
                            <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleUpload} disabled={!file || uploading}
                                className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold disabled:opacity-50">
                                {uploading ? '업로드 중...' : '업로드'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-[#f5f2f0] rounded-xl p-4 mb-4 text-[13px] text-[#2a1f1a]">
                            전체 {result.total}건 중 <span className="text-green-600 font-semibold">{result.success_count}건 성공</span>,
                            {' '}<span className="text-red-400 font-semibold">{result.fail_count}건 실패</span>
                        </div>
                        {result.failures.length > 0 && (
                            <div className="max-h-48 overflow-y-auto border border-[#e8e0dc] rounded-xl">
                                <table className="w-full text-[12px]">
                                    <thead>
                                        <tr className="bg-[#ede0d8]">
                                            <th className="text-left px-3 py-2">행</th>
                                            <th className="text-left px-3 py-2">바코드</th>
                                            <th className="text-left px-3 py-2">사유</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.failures.map((f, i) => (
                                            <tr key={i} className="border-t border-[#f5f2f0]">
                                                <td className="px-3 py-2 text-gray-400">{f.row}</td>
                                                <td className="px-3 py-2 text-gray-500">{f.barcode ?? '-'}</td>
                                                <td className="px-3 py-2 text-red-400">{f.reason}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <button onClick={onClose} className="w-full mt-5 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">
                            확인
                        </button>
                    </>
                )}
            </div>
        </div>
    )
}

